import * as React from "react";
import {
  Badge,
  FilterSearch,
  FiltersToolbar,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  formatDateTimeVn,
} from "@chotca/ui";
import { useAdminInvites } from "@chotca/api-client";
import type { AdminInviteItem } from "@chotca/config";
import { useTableFilters } from "@/lib/use-table-filters";
import { useTabParam } from "@/lib/use-tab-param";
import { DataTable, usePagination, type Column } from "@/components/data-table";

type InviteStatus = AdminInviteItem["status"];
type BadgeTone =
  | "lime-soft"
  | "sun-soft"
  | "info-soft"
  | "danger-soft"
  | "smoke";

// Each tab maps to one lifecycle status (or "all" = no status filter). The
// product calls an invite a "liên hệ"; these are the states an ops user wants
// to slice by — đã chốt (closed), đã mời (waiting), từ chối, etc.
const STATUS_TABS: { value: string; label: string; status?: InviteStatus }[] = [
  { value: "all", label: "Tất cả" },
  { value: "COMMITTED", label: "Đã chốt", status: "COMMITTED" },
  { value: "LOCKED", label: "Đã mời", status: "LOCKED" },
  { value: "ACCEPTED", label: "Đã nhận lịch", status: "ACCEPTED" },
  { value: "REJECTED", label: "Từ chối", status: "REJECTED" },
  { value: "EXPIRED", label: "Hết hạn", status: "EXPIRED" },
  { value: "CANCELLED", label: "Đã hủy", status: "CANCELLED" },
];

const STATUS_META: Record<InviteStatus, { label: string; tone: BadgeTone }> = {
  COMMITTED: { label: "Đã chốt", tone: "lime-soft" },
  LOCKED: { label: "Đã mời", tone: "sun-soft" },
  ACCEPTED: { label: "Đã nhận lịch", tone: "info-soft" },
  REJECTED: { label: "Từ chối", tone: "danger-soft" },
  EXPIRED: { label: "Hết hạn", tone: "smoke" },
  CANCELLED: { label: "Đã hủy", tone: "smoke" },
};

function startOfDayIso(date: string): string {
  return new Date(`${date}T00:00:00`).toISOString();
}
function endOfDayIso(date: string): string {
  return new Date(`${date}T23:59:59.999`).toISOString();
}

export function InvitesClient() {
  const [tab, setTab] = useTabParam("all");
  const activeStatus = STATUS_TABS.find((t) => t.value === tab)?.status;

  const filters = useTableFilters({
    search: { debounce: 300 },
    from: {},
    to: {},
  });
  const { page, pageSize, setPage, setPageSize } = usePagination();

  const fromIso = filters.applied.from
    ? startOfDayIso(filters.applied.from)
    : undefined;
  const toIso = filters.applied.to
    ? endOfDayIso(filters.applied.to)
    : undefined;

  const { data, isLoading, isFetching } = useAdminInvites({
    status: activeStatus,
    search: filters.applied.search,
    from: fromIso,
    to: toIso,
    page,
    limit: pageSize,
  });

  const list = (data?.items ?? []) as AdminInviteItem[];

  const columns: Column<AdminInviteItem>[] = [
    {
      key: "worker",
      header: "Ứng viên",
      cell: (i) => (
        <div className="min-w-0">
          <p className="line-clamp-1 font-semibold text-ink">
            {i.workerName ?? "Chưa rõ"}
          </p>
          <p className="text-[11px] text-ash">
            {i.source === "EMPLOYER_SOURCED"
              ? "DN chủ động tìm"
              : "Tự ứng tuyển"}
          </p>
        </div>
      ),
    },
    {
      key: "org",
      header: "Doanh nghiệp",
      cell: (i) => (
        <div className="min-w-0 max-w-52">
          <p className="line-clamp-1 text-sm text-ink">{i.orgName}</p>
          {i.addressAlias || i.district ? (
            <p className="line-clamp-1 text-[11px] text-ash">
              {[i.addressAlias, i.district].filter(Boolean).join(" · ")}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: "posting",
      header: "Tin tuyển dụng",
      cell: (i) => (
        <span className="line-clamp-1 block max-w-48 text-sm text-ash">
          {i.postingTitle}
        </span>
      ),
    },
    {
      key: "status",
      header: "Trạng thái",
      cell: (i) => {
        const m = STATUS_META[i.status];
        return <Badge tone={m.tone}>{m.label}</Badge>;
      },
    },
    {
      key: "contact",
      header: "Liên hệ",
      cell: (i) =>
        i.contact?.contactPhone ? (
          <div className="min-w-0">
            <p className="font-mono text-sm font-semibold text-ink">
              {i.contact.contactPhone}
            </p>
            {i.contact.zalo ? (
              <p className="text-[11px] text-ash">Zalo: {i.contact.zalo}</p>
            ) : null}
          </div>
        ) : (
          <span className="text-xs text-ash">—</span>
        ),
    },
    {
      key: "time",
      header: "Thời gian",
      align: "right",
      cell: (i) =>
        i.committedAt ? (
          <div className="text-right">
            <p className="text-xs font-semibold text-ink">
              {formatDateTimeVn(i.committedAt)}
            </p>
            <p className="text-[11px] text-ash">đã chốt</p>
          </div>
        ) : (
          <div className="text-right">
            <p className="text-xs text-ink">{formatDateTimeVn(i.createdAt)}</p>
            <p className="text-[11px] text-ash">đã mời</p>
          </div>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Liên hệ"
        description="Mọi lời mời phỏng vấn giữa doanh nghiệp và ứng viên: đã chốt, đã mời, từ chối và hơn thế."
      />

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList>
          {STATUS_TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={tab} className="space-y-4">
          <FiltersToolbar
            hasActiveFilters={filters.hasAny}
            onClearAll={filters.clear}
          >
            <FilterSearch
              value={filters.values.search ?? ""}
              onChange={(v) => filters.set("search", v)}
              placeholder="Tìm ứng viên, doanh nghiệp hoặc tin tuyển dụng..."
            />
            <DateInput
              value={filters.values.from ?? ""}
              onChange={(v) => filters.set("from", v)}
              ariaLabel="Từ ngày"
            />
            <DateInput
              value={filters.values.to ?? ""}
              onChange={(v) => filters.set("to", v)}
              ariaLabel="Đến ngày"
            />
          </FiltersToolbar>

          <DataTable
            columns={columns}
            data={list}
            isLoading={isLoading}
            isFetching={isFetching}
            rowKey={(i) => i.id}
            emptyText="Chưa có liên hệ nào khớp bộ lọc."
            page={page}
            pageSize={pageSize}
            total={data?.total}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DateInput({
  value,
  onChange,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  ariaLabel: string;
}) {
  return (
    <input
      type="date"
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-lg border border-smoke bg-paper px-3 text-sm text-ink outline-none focus:border-ink"
    />
  );
}
